import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { getRxDB } from '@/lib/db/rxdb'
import useLoginStore from '@/store/LoginStore'

/**
 * Navigate to whatever an entity link points at (task, project or doc).
 * Shared by doc link sections and the in-editor link chips.
 */
export function useEntityLinkNavigation() {
  const navigate = useNavigate()
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)

  return useCallback(
    async (entityId: string, entityType: string) => {
      if (!workspaceSlug) return

      if (entityType === ENTITY_TYPES.WORK_ITEM) {
        navigate({
          to: '/$workspaceSlug/tasks/$taskId',
          params: { workspaceSlug, taskId: entityId },
        })
        return
      }

      if (entityType === ENTITY_TYPES.PROJECT) {
        try {
          const db = getRxDB()
          const targetProject = await db.projects.findOne(entityId).exec()
          if (targetProject) {
            navigate({
              to: '/$workspaceSlug/projects/$projectKey',
              params: { workspaceSlug, projectKey: targetProject.projectKey },
            })
          }
        } catch {
          // Project not found
        }
        return
      }

      if (entityType === ENTITY_TYPES.DOCUMENT) {
        try {
          const db = getRxDB()
          const targetPage = await db.pages.findOne(entityId).exec()
          if (targetPage) {
            const targetProject = await db.projects
              .findOne(targetPage.projectId)
              .exec()
            if (targetProject) {
              navigate({
                to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
                params: {
                  workspaceSlug,
                  projectKey: targetProject.projectKey,
                  pageSlug: targetPage.slug,
                },
              })
            }
          }
        } catch {
          // Page or project not found
        }
      }
    },
    [workspaceSlug, navigate],
  )
}
